import { Button } from '@/components/ui/button';
import { CeldaProducto } from '@/components/ui/celda-producto';
import { CambioDePrecio } from '@/components/ui/precio';
import { TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatoFechaBolivia, formatoPrecio } from '@/lib/utils/formato-precio';
import { tituloDeSalida } from '@/features/pricing-rules/lib/salidas-sin-precio';
import { TextoRecortable } from './texto-recortable';
import { MOTIVOS_DE_DESCARTE } from './dialogo-descartar';
import type { PriceProposalRow } from '../types/price-proposals.types';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';

/**
 * Una fila de Descartadas (lote B1, spec 246 punto 3; maqueta `b1-descartar`):
 * Quién, Cuándo (con año y en hora de Bolivia, D10) y Motivo. Los descartes
 * automáticos dicen por qué solos; los de antes de que existiera el motivo, que
 * no se pudo dejar. Volver a Por decidir: la vieja recalcula al volver (spec
 * 275, R1-a), y la que no puede volver dice por qué.
 */

const minusculaInicial = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);

/** Se descartó sola: al vencer o al borrar su estrategia, o al quedar vieja (el motivo, o el retrato en un backend viejo). */
export function seDescartoSola(
  f: PriceProposalRow
): 'VENCIMIENTO' | 'BORRADO' | 'YA_NO_APLICA' | null {
  if (f.motivoDescarte === 'YA_NO_APLICA') return 'YA_NO_APLICA';
  if (f.motivoDescarte === 'VENCIO_SU_ESTRATEGIA') return 'VENCIMIENTO';
  if (f.motivoDescarte === 'SE_BORRO_SU_ESTRATEGIA') return 'BORRADO';
  return f.descartadaSola ?? null;
}

/** Lo que dice el detalle de «Ya no aplicaba» según por qué la descartó la corrida (ola final R1). */
const DETALLE_DE_YA_NO_APLICA: Record<
  NonNullable<PriceProposalRow['yaNoAplicabaCausa']>,
  string
> = {
  NO_PROPONE:
    'El precio o el costo cambió desde que se propuso y, al volver a calcularla, su estrategia ya no propuso moverla.',
  APLICO_OTRO:
    'Su estrategia ya aplicó sola otro precio a este producto, así que esta propuesta quedó sin efecto.',
  SIN_DATOS: 'Al volver a calcularla faltaron datos para proponer un precio, así que se descartó.',
  SE_APAGO:
    'Se apagó su estrategia. Al encenderla, su próxima corrida la vuelve a proponer con precios al día.',
  // Ronda del QA de R4: dar de baja también retira lo que esperaba (servidor).
  SE_DIO_DE_BAJA: 'Se dio de baja su estrategia: ya no la vuelve a proponer.',
};

/**
 * Ronda final de R4: la retirada al apagar promete «al encenderla…» sólo si su
 * estrategia sigue: dada de baja (sin estrategia) o vencida, ya no la propone.
 */
function detalleDeSeApago(f: PriceProposalRow): string {
  // null = la estrategia ya no existe; ausente = un servidor viejo (no se sabe).
  if (f.strategyApplicationId === null)
    return 'Se apagó su estrategia y después se dio de baja: ya no la vuelve a proponer.';
  if (f.estrategiaTerminoEl && new Date(f.estrategiaTerminoEl).getTime() < Date.now())
    return `Se apagó su estrategia y después terminó (el ${formatoFechaBolivia(f.estrategiaTerminoEl)}): ya no la vuelve a proponer.`;
  return DETALLE_DE_YA_NO_APLICA.SE_APAGO;
}

/** Lo que dice la columna Motivo: el motivo en negrita y, debajo, el detalle. */
export function motivoDeLaDescartada(f: PriceProposalRow): {
  titulo: string | null;
  detalle: string | null;
  apagado: boolean;
} {
  const sola = seDescartoSola(f);
  if (sola === 'VENCIMIENTO')
    return {
      titulo: 'Venció su estrategia',
      detalle: f.strategyName
        ? f.estrategiaTerminoEl
          ? `«${f.strategyName}» terminó el ${formatoFechaBolivia(f.estrategiaTerminoEl)}.`
          : `«${f.strategyName}» terminó.`
        : null,
      apagado: false,
    };
  if (sola === 'BORRADO')
    return {
      titulo: 'Se borró su estrategia',
      detalle: f.strategyName ? `«${f.strategyName}» se borró.` : null,
      apagado: false,
    };
  if (sola === 'YA_NO_APLICA')
    return {
      // Spec 294 (D-R4-1): el texto del spec para la que se retiró al apagar.
      titulo:
        f.yaNoAplicabaCausa === 'SE_APAGO'
          ? 'Ya no aplica: se apagó la estrategia'
          : f.yaNoAplicabaCausa === 'SE_DIO_DE_BAJA'
            ? 'Ya no aplica: se dio de baja la estrategia'
            : 'Ya no aplicaba',
      detalle:
        f.yaNoAplicabaCausa === 'SE_APAGO'
          ? detalleDeSeApago(f)
          : DETALLE_DE_YA_NO_APLICA[f.yaNoAplicabaCausa ?? 'NO_PROPONE'],
      apagado: false,
    };
  const elegido = MOTIVOS_DE_DESCARTE.find((m) => m.clave === f.motivoDescarte);
  if (elegido)
    return {
      titulo: elegido.texto,
      detalle: f.notaDescarte ? `«${f.notaDescarte}»` : null,
      apagado: false,
    };
  // «No mover» en la ventana Decidir: la salida elegida es el porqué.
  if (f.salidaElegida)
    return {
      titulo: null,
      detalle: `Se eligió: ${minusculaInicial(tituloDeSalida(f.salidaElegida, f.rangos))}`,
      apagado: false,
    };
  if (f.motivoDescarte === 'SIN_MOTIVO')
    return { titulo: null, detalle: 'Sin motivo', apagado: true };
  return {
    titulo: null,
    detalle: 'Sin motivo: se descartó antes de que se pudiera dejar uno.',
    apagado: true,
  };
}

/** Las celdas de una descartada (la fila la pone la tabla). */
export function CeldasDeDescartada({
  f,
  trabajando,
  onVolver,
}: {
  f: PriceProposalRow;
  trabajando: boolean;
  onVolver: (id: string) => void;
}) {
  // Soporte («Entrar como») en una demo sólo mira: no aprueba, descarta ni devuelve propuestas.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  const sola = seDescartoSola(f);
  // Descartada con «no mover»: no se propuso ningún precio.
  const sinPrecioPropuesto = f.salidaElegida === 'NO_MOVER';
  const motivo = motivoDeLaDescartada(f);
  return (
    <>
      <TableCell className="pl-5 align-middle">
        <CeldaProducto
          nombre={
            <span className="line-clamp-2 [overflow-wrap:anywhere]" title={f.productName}>
              {f.productName}
            </span>
          }
          detalle={[f.sku, f.category, f.brand].filter(Boolean).join(' · ')}
        />
      </TableCell>
      <TableCell className="tabular-nums align-middle text-right font-semibold">
        {sinPrecioPropuesto ? '—' : formatoPrecio(f.priceAfter)}
      </TableCell>
      <TableCell className="align-middle text-right">
        {sinPrecioPropuesto ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <CambioDePrecio diferencia={f.diferencia} porcentaje={f.diferenciaPct} />
        )}
      </TableCell>
      <TableCell className="align-middle text-sm">
        {sola ? (
          <span className="text-muted-foreground">Se descartó sola</span>
        ) : f.resueltaPor ? (
          f.resueltaPor
        ) : (
          <span className="text-muted-foreground">No quedó registrado</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap align-middle text-sm">
        {formatoFechaBolivia(f.resolvedAt ?? f.createdAt, { hora: true })}
      </TableCell>
      <TableCell className="max-w-xs align-middle text-sm">
        {motivo.titulo && <div className="font-medium">{motivo.titulo}</div>}
        {motivo.detalle && (
          <TextoRecortable
            texto={motivo.detalle}
            className={cn(
              'text-xs',
              motivo.apagado ? 'text-muted-foreground' : 'text-foreground/80'
            )}
          />
        )}
      </TableCell>
      <TableCell className="align-middle text-right">
        {f.sePuedeVolver ? (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            disabled={trabajando}
            title={
              f.vuelveRecalculada
                ? 'El precio o el costo cambió desde que se propuso: se recalcula con su estrategia y las reglas de hoy.'
                : undefined
            }
            onClick={() => onVolver(f.id)}
            {...sinCambios}
          >
            {f.vuelveRecalculada ? 'Recalcular y volver' : 'Volver a Por decidir'}
          </Button>
        ) : f.porQueNoVuelve ? (
          <span className="block min-w-0 max-w-[16rem] text-left text-xs [overflow-wrap:anywhere] text-muted-foreground">
            {f.porQueNoVuelve}
          </span>
        ) : null}
      </TableCell>
    </>
  );
}
